import React from 'react';
import { useAuth } from '../Auth/AuthProvider';
import { useBookChapterContext } from '../ReadBook/Context/BookChapterContextProvider';
import { Book } from '../Types/Book';
import { BookChapter } from '../Types/BookChapter';

// Define an enum for collaboration status
export enum CollaborationStatus {
  WAITING = 'waiting',
  ACTION = 'action',
  COMPLETED = 'completed',
}

interface CollaborationStatusTagProps {
  bookProp?: Book;
  chapterProp?: BookChapter;
}

/**
 * A component that displays the collaboration status with appropriate styling
 * and computes its own message based on the current state
 */
const CollaborationStatusTag: React.FC<CollaborationStatusTagProps> = ({
  bookProp,
  chapterProp,
}) => {
  const { user } = useAuth();
  const contextValues = useBookChapterContext();

  // Use props if provided, otherwise fall back to context
  const book = bookProp || contextValues.book;
  const chapter = chapterProp || contextValues.chapter;

  const coAuthor = user && book?.getMyCoAuthor(user.id);

  if (!user || !book || !coAuthor || !chapter || book.isAbandoned()) {
    return null;
  }

  const nextChapter = book.getChapter(chapter.chapterNumber + 1);
  const isMyChapter = chapter.article?.createdByUserId === user?.id;
  const isMyTurn = chapter.isMyTurn(user?.id);

  // Determine status based on chapter and book state
  const getStatus = (): CollaborationStatus => {
    if (chapter.isLastChapter()) {
      return CollaborationStatus.COMPLETED;
    }
    if (nextChapter) {
      return CollaborationStatus.COMPLETED;
    }
    if (isMyTurn) {
      return CollaborationStatus.ACTION;
    }

    return CollaborationStatus.WAITING;
  };

  const status = getStatus();

  // Blue when it's my chapter, Purple when it's co-author's chapter
  const pillStyle =
    status === CollaborationStatus.COMPLETED && isMyChapter
      ? 'bg-blue-100 text-blue-700'
      : 'bg-purple-100 text-purple-700';

  return (
    <div
      className={`flex self-start items-center gap-1 px-3 py-1 rounded-full ${pillStyle} text-sm whitespace-nowrap`}
    >
      <span className="font-semibold">
        {status === CollaborationStatus.COMPLETED ? (
          isMyChapter ? (
            'Chapter created by you'
          ) : (
            `Chapter created by ${coAuthor.name}`
          )
        ) : isMyTurn ? (
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-purple-600 mr-1"></div>
            {coAuthor.name} is waiting for you to finish the chapter...
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-purple-600 mr-1"></div>
            Waiting for {coAuthor.name}...
          </div>
        )}
      </span>
    </div>
  );
};

export default CollaborationStatusTag;
